"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { ChevronDown, ChevronUp, Eye, PictureInPicture2, X } from "lucide-react";
import { useEnrollmentFloater } from "@/app/_module/contexts/EnrollmentFloaterContext";

/**
 * Global enrollment surface, mounted at the authenticated layout level so
 * the iframe persists across page navigation. Two visual modes share ONE
 * underlying iframe — flipping between them does NOT reload the eApp.
 *
 * Floating-mode rendering follows the same pattern as the ScriptPanel:
 *   - Position + size live in refs (NOT state) so dragging doesn't trigger
 *     React renders that would force the iframe to re-layout 60×/sec.
 *   - Movement applied via `style.transform = translate3d(x,y,0)` — GPU-
 *     accelerated, doesn't trigger layout, smooth even with a heavy iframe.
 *   - Mousemove handler is coalesced through `requestAnimationFrame`.
 *   - Position + size persisted to `localStorage` per browser tab so the
 *     agent's preferred corner sticks across sessions.
 *   - Clamped to viewport so the title bar can never be dragged off-screen.
 */
const POS_KEY = "enrollmentFloater.pos";
const SIZE_KEY = "enrollmentFloater.size";
const TRANSLUCENCY_KEY = "enrollmentFloater.translucency";

/**
 * Three-step translucency cycle. The floater stays fully opaque on hover so
 * the agent can interact with it normally — the see-through state only
 * applies when their cursor is elsewhere. Lets the agent peek at lead notes
 * or the dialer behind the application without dismissing it.
 */
const TRANSLUCENCY_LEVELS = [
  { label: "Opaque", idle: 1.0 },
  { label: "Translucent", idle: 0.65 },
  { label: "See-through", idle: 0.35 },
];
const MIN_SIZE = { width: 320, height: 360 };
const DEFAULT_SIZE = { width: 560, height: 720 };
const MINIMIZED_HEIGHT = 44;
const MINIMIZED_WIDTH = 280;

function loadJSON<T>(key: string): T | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function saveJSON(key: string, value: any) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* swallow quota / privacy errors */
  }
}
function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function getDefaultPos() {
  const w = typeof window !== "undefined" ? window.innerWidth : 1280;
  const h = typeof window !== "undefined" ? window.innerHeight : 800;
  return {
    x: Math.max(0, w - DEFAULT_SIZE.width - 24),
    y: Math.max(0, h - DEFAULT_SIZE.height - 24),
  };
}

export default function GlobalEnrollmentFloater() {
  const {
    open,
    mode,
    session,
    popupOpen,
    popupSession,
    maximize,
    close,
    closePopup,
    focusPopup,
  } = useEnrollmentFloater();
  const router = useRouter();
  const pathname = usePathname();

  // Dock-back button: modal mode renders INLINE on /quote-enroll only, so
  // docking from any other page must also navigate there — otherwise the
  // eApp just vanishes. Client-side nav keeps the context (and iframe
  // session) alive; the page renders the embed on arrival.
  const dockToQuoteEnroll = useCallback(() => {
    maximize();
    // Shell-aware: docking must land on /m/quote-enroll on the shell —
    // pushing the desktop route loaded the whole DESKTOP app inside the phone.
    const target = pathname?.startsWith("/m") ? "/m/quote-enroll" : "/quote-enroll";
    if (!pathname?.startsWith(target)) {
      router.push(target);
    }
  }, [maximize, pathname, router]);

  // Refs (NOT state) so drag/resize don't cause React re-renders.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const pos = useRef(loadJSON<{ x: number; y: number }>(POS_KEY) || getDefaultPos());
  const sz = useRef(loadJSON<{ width: number; height: number }>(SIZE_KEY) || DEFAULT_SIZE);

  const dragRef = useRef<{
    active: boolean;
    sx: number;
    sy: number;
    ox: number;
    oy: number;
  }>({ active: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const resizeRef = useRef<{
    active: boolean;
    sx: number;
    sy: number;
    ow: number;
    oh: number;
    ox: number;
    oy: number;
    /** Which edge/corner the user grabbed. */
    dir: "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw" | null;
  }>({ active: false, sx: 0, sy: 0, ow: 0, oh: 0, ox: 0, oy: 0, dir: null });
  const rafRef = useRef(0);

  // Minimized is a state because it changes the rendered tree (hides iframe),
  // but it's cheap — only flips on user click, not during drag.
  const [minimized, setMinimized] = useState(false);

  // Translucency level (0=opaque, 1=mid, 2=see-through). Persisted across
  // sessions. Hover restores full opacity for interaction.
  const [translucencyIdx, setTranslucencyIdx] = useState<number>(() => {
    const saved = loadJSON<number>(TRANSLUCENCY_KEY);
    return typeof saved === "number" && saved >= 0 && saved < TRANSLUCENCY_LEVELS.length
      ? saved
      : 0;
  });
  const [hovered, setHovered] = useState(false);
  const idleOpacity = TRANSLUCENCY_LEVELS[translucencyIdx]?.idle ?? 1;
  const currentOpacity = hovered || idleOpacity === 1 ? 1 : idleOpacity;

  /** Apply current pos/size directly to the DOM, no React render. */
  const applyDOM = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    // Clear any docked-overlay coordinates left over from modal mode.
    el.style.left = "0px";
    el.style.top = "0px";
    el.style.transform = `translate3d(${pos.current.x}px, ${pos.current.y}px, 0)`;
    el.style.width = (minimized ? MINIMIZED_WIDTH : sz.current.width) + "px";
    el.style.height = (minimized ? MINIMIZED_HEIGHT : sz.current.height) + "px";
  }, [minimized]);

  // ── ONE iframe, ever. ──
  // The application iframe must NEVER unmount while a session is open —
  // any remount reloads the eApp and Techficient re-enters the wizard at
  // the last committed section ("I was at 60%, it reset to 20%"). So the
  // floater owns the iframe in BOTH modes; "modal" just pins this same
  // container over the Quote & Enroll page's embed slot. The rAF loop
  // tracks the slot's rect (scroll/resize/layout shifts included).
  const [modalDocked, setModalDocked] = useState(false);
  const modalDockedRef = useRef(false);
  useEffect(() => {
    if (!open || mode !== "modal") {
      if (modalDockedRef.current) {
        modalDockedRef.current = false;
        setModalDocked(false);
      }
      return;
    }
    let raf = 0;
    const track = () => {
      const slot = document.getElementById("enrollment-embed-slot");
      const el = containerRef.current;
      if (slot && el) {
        const r = slot.getBoundingClientRect();
        el.style.transform = "none";
        el.style.left = `${r.left}px`;
        el.style.top = `${r.top}px`;
        el.style.width = `${r.width}px`;
        el.style.height = `${r.height}px`;
        if (!modalDockedRef.current) {
          modalDockedRef.current = true;
          setModalDocked(true);
        }
      } else if (modalDockedRef.current) {
        // Slot vanished (navigated off /quote-enroll while docked) — fall
        // back to the floating presentation without touching the iframe.
        modalDockedRef.current = false;
        setModalDocked(false);
      }
      raf = requestAnimationFrame(track);
    };
    raf = requestAnimationFrame(track);
    return () => cancelAnimationFrame(raf);
  }, [open, mode]);

  const presentingModal = mode === "modal" && modalDocked;

  // Re-apply floating DOM whenever we're NOT docked into the page slot.
  useEffect(() => {
    if (open && !presentingModal) applyDOM();
  }, [mode, open, applyDOM, presentingModal]);

  // Shell fit: desktop-persisted geometry (900px-wide floaters) overflows a
  // phone. Whenever the stored size can't fit the viewport, snap to a
  // phone-sheet: near-full width between the app header and the dock. The
  // user's own drags/resizes within the phone are kept.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.location.pathname.startsWith("/m")) return;
    const maxW = window.innerWidth - 12;
    if (sz.current.width > maxW) {
      pos.current = { x: 6, y: 108 };
      sz.current = {
        width: maxW,
        height: Math.max(420, window.innerHeight - 108 - 150),
      };
      applyDOM();
    }
  }, [applyDOM, open]);

  // Keep clamped to viewport on window resize.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onResize() {
      pos.current.x = clamp(pos.current.x, 0, window.innerWidth - 120);
      pos.current.y = clamp(pos.current.y, 0, window.innerHeight - 40);
      applyDOM();
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [applyDOM]);

  // Global mouse handlers — set once, refs decide whether to act.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function applyPointer(clientX: number, clientY: number) {
      if (!dragRef.current.active && !resizeRef.current.active) return;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (dragRef.current.active) {
          const d = dragRef.current;
          pos.current.x = clamp(
            d.ox + (clientX - d.sx),
            0,
            window.innerWidth - 120,
          );
          pos.current.y = clamp(
            d.oy + (clientY - d.sy),
            0,
            window.innerHeight - 40,
          );
        }
        if (resizeRef.current.active) {
          const r = resizeRef.current;
          const dx = clientX - r.sx;
          const dy = clientY - r.sy;
          const dir = r.dir;
          // Update width / x for east-west components.
          if (dir && (dir === "e" || dir === "ne" || dir === "se")) {
            sz.current.width = clamp(
              r.ow + dx,
              MIN_SIZE.width,
              window.innerWidth - pos.current.x - 8,
            );
          }
          if (dir && (dir === "w" || dir === "nw" || dir === "sw")) {
            const proposedWidth = clamp(r.ow - dx, MIN_SIZE.width, r.ox + r.ow - 8);
            const widthDelta = r.ow - proposedWidth;
            sz.current.width = proposedWidth;
            pos.current.x = clamp(r.ox + widthDelta, 0, window.innerWidth - 120);
          }
          // Update height / y for north-south components.
          if (dir && (dir === "s" || dir === "se" || dir === "sw")) {
            sz.current.height = clamp(
              r.oh + dy,
              MIN_SIZE.height,
              window.innerHeight - pos.current.y - 8,
            );
          }
          if (dir && (dir === "n" || dir === "ne" || dir === "nw")) {
            const proposedHeight = clamp(r.oh - dy, MIN_SIZE.height, r.oy + r.oh - 8);
            const heightDelta = r.oh - proposedHeight;
            sz.current.height = proposedHeight;
            pos.current.y = clamp(r.oy + heightDelta, 0, window.innerHeight - 40);
          }
        }
        applyDOM();
      });
    }
    function onMove(e: MouseEvent) {
      applyPointer(e.clientX, e.clientY);
    }
    // Touch mirror — the shell drags with a finger. Non-passive so an active
    // drag can preventDefault the page scroll underneath.
    function onTouchMove(e: TouchEvent) {
      if (!dragRef.current.active && !resizeRef.current.active) return;
      const t = e.touches[0];
      if (!t) return;
      e.preventDefault();
      applyPointer(t.clientX, t.clientY);
    }
    function onUp() {
      if (dragRef.current.active) {
        dragRef.current.active = false;
        saveJSON(POS_KEY, pos.current);
      }
      if (resizeRef.current.active) {
        resizeRef.current.active = false;
        resizeRef.current.dir = null;
        saveJSON(SIZE_KEY, sz.current);
        saveJSON(POS_KEY, pos.current);
        document.body.style.cursor = "";
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);
      cancelAnimationFrame(rafRef.current);
    };
  }, [applyDOM]);

  const onDragStart = (e: React.MouseEvent) => {
    // Ignore clicks on header buttons (they have their own handlers + stop propagation).
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    dragRef.current = {
      active: true,
      sx: e.clientX,
      sy: e.clientY,
      ox: pos.current.x,
      oy: pos.current.y,
    };
  };

  const onDragTouchStart = (e: React.TouchEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    const t = e.touches[0];
    if (!t) return;
    dragRef.current = {
      active: true,
      sx: t.clientX,
      sy: t.clientY,
      ox: pos.current.x,
      oy: pos.current.y,
    };
  };

  const cursorForDir: Record<string, string> = {
    n: "ns-resize",
    s: "ns-resize",
    e: "ew-resize",
    w: "ew-resize",
    ne: "nesw-resize",
    sw: "nesw-resize",
    nw: "nwse-resize",
    se: "nwse-resize",
  };

  const onResizeStart =
    (dir: "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw") =>
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        active: true,
        sx: e.clientX,
        sy: e.clientY,
        ow: sz.current.width,
        oh: sz.current.height,
        ox: pos.current.x,
        oy: pos.current.y,
        dir,
      };
      document.body.style.cursor = cursorForDir[dir];
    };

  // Popup surface: the carrier step lives in a separate browser window
  // (OAuth-style) because carrier sites block framing. In-app we render a
  // status pill — INDEPENDENT of the iframe surface, so the Techficient
  // case view can stay embedded behind the popup. Bottom-center so it never
  // collides with the floating iframe's bottom-right corner.
  const popupPill =
    popupOpen && popupSession ? (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[95] bg-white border-2 border-primary rounded-full shadow-2xl flex items-center gap-2 pl-4 pr-2 py-2">
        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <div className="min-w-0 max-w-[220px]">
          <p className="text-[11px] font-semibold text-gray-900 truncate">
            {popupSession.carrier || "Carrier"} application in progress
          </p>
          <p className="text-[9px] text-gray-500 truncate">
            Finishing in a separate window
          </p>
        </div>
        <button
          type="button"
          onClick={focusPopup}
          className="text-[10px] font-semibold rounded-full px-2.5 py-1.5 bg-gray-900 text-white hover:bg-gray-800 flex-shrink-0"
          title="Bring the application window to the front"
        >
          Bring to front
        </button>
        <button
          type="button"
          onClick={closePopup}
          className="text-gray-500 hover:text-red-600 p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
          title="Close application window"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    ) : null;

  if (!open || !session?.launchUrl) return popupPill;

  // Shared iframe — keyed by launchUrl so it doesn't reload on mode switches.
  const sharedIframe = (
    <iframe
      key={session.launchUrl}
      src={session.launchUrl}
      title="Insurance Application"
      className="w-full h-full border-0"
      // no-referrer: launchUrl carries a self-authenticating token — keep it
      // out of the Referer header on any subresource the eApp fetches.
      referrerPolicy="no-referrer"
      // allow-popups kept: we EXPERIMENTED with removing it (2026-07-17) to
      // block tab escapes from inside the embed, but the browser blocks
      // WITHOUT telling us the URL — their in-page buttons just die
      // silently, which tested worse than the stray tab. The real fix is
      // the Techficient postMessage bridge (spec sent); until then their
      // internal links open tabs and ours open in-app.
      sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-modals allow-downloads allow-storage-access-by-user-activation"
    />
  );

  // ONE container, both modes. Docked ("modal") pins over the page's
  // embed slot with no chrome — the page card supplies the header. The
  // iframe element itself is IDENTICAL across mode flips: no unmount, no
  // reload, wizard position preserved.
  return (
    <>
    {popupPill}
    <div
      ref={containerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={
        presentingModal
          ? "fixed z-[40] bg-white flex flex-col"
          : "fixed left-0 top-0 z-[90] bg-white border-2 border-primary rounded-lg shadow-2xl flex flex-col select-none will-change-transform transition-opacity duration-200"
      }
      style={
        presentingModal
          ? { opacity: 1 }
          : {
              // Initial values inlined so first paint matches refs;
              // subsequent updates come from applyDOM() via translate3d.
              transform: `translate3d(${pos.current.x}px, ${pos.current.y}px, 0)`,
              width: (minimized ? MINIMIZED_WIDTH : sz.current.width) + "px",
              height: (minimized ? MINIMIZED_HEIGHT : sz.current.height) + "px",
              opacity: currentOpacity,
            }
      }
    >
      {/* Drag handle / header — kept MOUNTED in docked mode (display:none)
          so the iframe's sibling position never changes → never remounts. */}
      <div
        style={presentingModal ? { display: "none" } : { touchAction: "none" }}
        className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-primary/5 rounded-t-md cursor-grab active:cursor-grabbing"
        onMouseDown={onDragStart}
        onTouchStart={onDragTouchStart}
      >
        <div className="min-w-0 flex-1 pointer-events-none">
          <p className="text-[11px] font-semibold text-gray-900 truncate">
            {session.carrier || "Carrier"} — {session.product || "Application"}
          </p>
          {!minimized && (
            <p className="text-[9px] text-gray-500 truncate">
              Drag header to move · Application stays open across pages
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Restore (only shown when minimized) — chevron pointing up reads
              clearly as "expand back out". */}
          {minimized && (
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => setMinimized(false)}
              className="text-gray-500 hover:text-gray-900 p-1 rounded hover:bg-white"
              title="Restore"
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
          )}
          {/* Minimize + translucency + dock-back-to-page (only shown when
              NOT minimized). */}
          {!minimized && (
            <>
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  const next = (translucencyIdx + 1) % TRANSLUCENCY_LEVELS.length;
                  setTranslucencyIdx(next);
                  saveJSON(TRANSLUCENCY_KEY, next);
                }}
                className={`p-1 rounded hover:bg-white ${
                  translucencyIdx === 0
                    ? "text-gray-500 hover:text-gray-900"
                    : "text-primary hover:text-primary/80"
                }`}
                title={`Translucency: ${TRANSLUCENCY_LEVELS[translucencyIdx].label} (click to cycle)`}
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setMinimized(true)}
                className="text-gray-500 hover:text-gray-900 p-1 rounded hover:bg-white"
                title="Minimize"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={dockToQuoteEnroll}
                className="text-gray-500 hover:text-gray-900 p-1 rounded hover:bg-white"
                title="Dock back into Quote & Enroll page"
              >
                <PictureInPicture2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={close}
            className="text-gray-500 hover:text-red-600 p-1 rounded hover:bg-white"
            title="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* iframe */}
      {(!minimized || presentingModal) && (
        <div ref={innerRef} className="flex-1 relative bg-white overflow-hidden">
          {/*
            iframe-blocking overlay during drag/resize: iframes swallow mouse
            events when the pointer enters them, which would cancel a drag
            mid-motion. The overlay catches the events so the parent's
            mousemove keeps firing all the way to mouseup. Pure CSS — only
            paints on body's dragging/resizing state.
          */}
          {sharedIframe}
          <DragOverlay />

          {/* 8 resize handles — floating mode only (docked mode sizes to
              the page slot). Rendered AFTER the iframe so toggling them
              can't shift the iframe's child index. */}
          {!presentingModal && (
          <>
          {/* Edges */}
          <div
            className="absolute top-0 left-2 right-2 h-1.5 cursor-ns-resize z-10 hover:bg-primary/20"
            onMouseDown={onResizeStart("n")}
            title="Resize from top"
          />
          <div
            className="absolute bottom-0 left-2 right-2 h-1.5 cursor-ns-resize z-10 hover:bg-primary/20"
            onMouseDown={onResizeStart("s")}
            title="Resize from bottom"
          />
          <div
            className="absolute top-2 bottom-2 left-0 w-1.5 cursor-ew-resize z-10 hover:bg-primary/20"
            onMouseDown={onResizeStart("w")}
            title="Resize from left"
          />
          <div
            className="absolute top-2 bottom-2 right-0 w-1.5 cursor-ew-resize z-10 hover:bg-primary/20"
            onMouseDown={onResizeStart("e")}
            title="Resize from right"
          />
          {/* Corners */}
          <div
            className="absolute top-0 left-0 w-3 h-3 cursor-nwse-resize z-20"
            onMouseDown={onResizeStart("nw")}
          />
          <div
            className="absolute top-0 right-0 w-3 h-3 cursor-nesw-resize z-20"
            onMouseDown={onResizeStart("ne")}
          />
          <div
            className="absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize z-20"
            onMouseDown={onResizeStart("sw")}
          />
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-20"
            style={{
              background:
                "linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.15) 50%)",
            }}
            onMouseDown={onResizeStart("se")}
          />
          </>
          )}
        </div>
      )}
    </div>
    </>
  );
}

/**
 * Invisible overlay that captures pointer events while a drag/resize is
 * in progress. Without this, dragging *over the iframe* hands the event
 * to the iframe's document and our parent's mousemove stops firing, which
 * makes drag feel "sticky" once the cursor crosses into the iframe area.
 *
 * Wired off `cursor` set on body during the resize handler — same trick
 * applies during drag because `dragRef.active` causes the cursor to stay
 * grabbing. We add a lightweight always-mounted div that only intercepts
 * when JS says so.
 */
function DragOverlay() {
  const [active, setActive] = useState(false);
  useEffect(() => {
    function onDown() {
      // delay one frame to let the drag handlers set their refs first
      requestAnimationFrame(() => setActive(true));
    }
    function onUp() {
      setActive(false);
    }
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);
  return (
    <div
      className="absolute inset-0 z-[5]"
      style={{
        pointerEvents: active ? "auto" : "none",
        background: "transparent",
      }}
    />
  );
}
